import { LinkedItem, LinkedSon } from '../../../types.d'
import { useAppStore } from '@store'
import { sceneGLBLookupKey } from '@context/utils/linked/buildLinkedCatalogId'
import { itemUsesAccesorioFregaderoCatalog } from '@utils/sceneObjects/isAccesorioFregaderoCatalog'
import { getCatalogGlbAabbMm } from '@utils/sceneObjects/items/getGLBSize'
import {
  applyLinkedApplianceLocalTransformFromApi,
  linkedSonKeepsApiLocalTransform,
  usesFreeDimensionScaling,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'

/**
 * Calcula el incremento para fregaderos basándose en el acabado de la encimera (valores positivos)
 * @param acabado - El acabado de la encimera
 * @returns El incremento en píxeles (positivo)
 */
function getFregaderoIncrement(acabado: string | undefined): number {
  if (!acabado) return 0

  switch (acabado) {
    case 'Compacto':
      return 0
    case 'Postformadas':
      return 20
    case 'Porcelanicas 20':
      return 10
    case 'Porcelanicas 12':
      return 2
    default:
      return 0
  }
}

/**
 * Calcula el incremento para grifos basándose en el acabado de la encimera (valores positivos)
 * @param acabado - El acabado de la encimera
 * @returns El incremento en píxeles (positivo)
 */
function getGrifoIncrement(acabado: string | undefined): number {
  if (!acabado) return 0

  switch (acabado) {
    case 'Compacto':
      return 0
    case 'Postformadas':
      return 20
    case 'Porcelanicas 20':
      return 10
    case 'Porcelanicas 12':
      return 2
    default:
      return 0
  }
}

/**
 * Ajusta la posición de elementos hijos (Placa, Fregadero, Grifo) basándose en la Encimera
 * @param parentItem - El elemento padre linkedItem
 * @param children - Array de elementos hijos linkedSon
 * @param setItem - Función para actualizar un item en el store (opcional)
 */
export function adjustChildrenToEncimera(
  parentItem: LinkedItem,
  children: LinkedSon[],
  setItem?: (id: string, updates: Partial<any>) => void,
): void {
  const encimera = children.find(
    (child) =>
      child.typeOfSon === 'Encimera' || child.description?.toLowerCase().includes('encimera'),
  )

  if (!encimera) {
    return
  }

  const encimeraY = encimera.y ?? 0

  let encimeraHeight = encimera.height ?? 0
  const acabado = encimera.textures?.[0]?.acabado

  if (acabado) {
    if (acabado === 'Porcelanicas 12') {
      encimeraHeight = 12
    } else if (acabado === 'Porcelanicas 20') {
      encimeraHeight = 20
    } else if (acabado === 'Postformadas') {
      encimeraHeight = 30
    } else if (acabado === 'Compacto') {
      encimeraHeight = 10
    }
  }

  children.forEach((child) => {
    // COMP-GRIFO / COMP-BAJOENC no se sientan en la encimera. "ORIFICIO GRIFO" coincide con /grifo/.
    if (itemUsesAccesorioFregaderoCatalog(child as { catalogID?: string; codigo?: string })) return

    const isPlaca =
      child.typeOfSon === 'Placa' || child.description?.toLowerCase().includes('placa')
    const isFregadero =
      child.typeOfSon === 'Fregadero' || child.description?.toLowerCase().includes('fregadero')
    const isGrifo =
      child.typeOfSon === 'Grifo' || child.description?.toLowerCase().includes('grifo')

    if ((isPlaca || isFregadero || isGrifo) && child.id !== encimera.id) {
      // Free-dim: Y API → local vía helper (nunca encimeraY+grosor, que rompe placas embebidas
      // y proyectos legacy con Y ya transformada).
      if (
        usesFreeDimensionScaling(parentItem) &&
        linkedSonKeepsApiLocalTransform(child as any)
      ) {
        // El item del store sale congelado (immer). Hay que escribir en una copia.
        const draft = { ...child }
        applyLinkedApplianceLocalTransformFromApi(
          draft as any,
          parentItem as any,
          encimera as never,
          {
            encimeraAabb: getCatalogGlbAabbMm(
              useAppStore.getState().sceneGLB as never,
              encimera.catalogID,
              [
                sceneGLBLookupKey(String(encimera.catalogID ?? '')),
                (encimera as { idUnidades?: number }).idUnidades,
              ],
            ),
          },
        )
        if (setItem) {
          setItem(child.id, {
            x: draft.x,
            y: draft.y,
            z: draft.z,
            posicionX: (draft as any).posicionX,
            posicionY: (draft as any).posicionY,
            posicionZ: (draft as any).posicionZ,
            baseY: (draft as any).baseY,
          })
        }
        return
      }

      const originalY = child.y ?? 0
      let newY = encimeraY + encimeraHeight
      let needsBaseYUpdate = false
      let baseY = originalY

      if (isFregadero || isGrifo) {
        if (child.baseY !== undefined) {
          baseY = child.baseY
        } else {
          baseY = originalY
          needsBaseYUpdate = true
        }

        const positionDifference = Math.abs(originalY - baseY)
        if (positionDifference > 50) {
          baseY = originalY
          needsBaseYUpdate = true
        }

        const increment = isFregadero ? getFregaderoIncrement(acabado) : getGrifoIncrement(acabado)
        newY = baseY + increment
      }

      if (setItem) {
        const updates: any = { y: newY }
        if ((isFregadero || isGrifo) && needsBaseYUpdate) {
          updates.baseY = baseY
        }
        setItem(child.id, updates)
      } else {
        child.y = newY
        if ((isFregadero || isGrifo) && needsBaseYUpdate) {
          child.baseY = baseY
        }
      }
    }
  })
}

/**
 * Función que ajusta las posiciones de los hijos basándose en la encimera
 * Se llama desde el store con acceso a los items
 * @param parentItem - El elemento padre linkedItem
 * @param getItem - Función para obtener un item del store por ID
 * @param setItem - Función para actualizar un item en el store
 */
export function adjustChildrenPositionsAfterInheritance(
  parentItem: LinkedItem,
  getItem: (id: string) => any,
  setItem: (id: string, updates: Partial<any>) => void,
): void {
  if (!parentItem.childs || parentItem.childs.size === 0) {
    return
  }

  const encimeraId = Array.from(parentItem.childs).find((childId) => {
    const child = getItem(childId)
    return (
      child &&
      (child.typeOfSon === 'Encimera' || child.description?.toLowerCase().includes('encimera'))
    )
  })

  if (!encimeraId) {
    return
  }

  const encimera = getItem(encimeraId)
  if (!encimera) return

  // const encimeraY = encimera.y ?? 0

  // let encimeraHeight = encimera.height ?? 0
  // const acabado = encimera.textures?.[0]?.acabado

  // if (acabado) {
  //   if (acabado === 'Porcelanicas 12') {
  //     encimeraHeight = 12
  //   } else if (acabado === 'Porcelanicas 20') {
  //     encimeraHeight = 20
  //   } else if (acabado === 'Postformadas') {
  //     encimeraHeight = 30
  //   } else if (acabado === 'Compacto') {
  //     encimeraHeight = 10
  //   }
  // }

  const childrenArray = Array.from(parentItem.childs)
    .map((childId) => getItem(childId))
    .filter(Boolean) as LinkedSon[]

  if (childrenArray.length === 0) {
    return
  }

  adjustChildrenToEncimera(parentItem, childrenArray, setItem)
}
